import type { WeavableTheme } from '../types';

/**
 * Maps WeavableTheme keys to CSS variable names.
 */
const themeMap: Record<keyof WeavableTheme, string> = {
    // Global
    primaryColor: '--weavable-primary-color',
    textColor: '--weavable-text-color',
    textSecondary: '--weavable-text-secondary',
    errorColor: '--weavable-error-color',
    skeletonBg: '--weavable-skeleton-bg',
    skeletonShimmer: '--weavable-skeleton-shimmer',
    spacingXs: '--weavable-spacing-xs',
    spacingSm: '--weavable-spacing-sm',
    spacingMd: '--weavable-spacing-md',
    spacingLg: '--weavable-spacing-lg',

    // Section
    sectionBgColor: '--weavable-section-bg-color',
    sectionPadding: '--weavable-section-padding',
    sectionBorderRadius: '--weavable-section-border-radius',
    sectionBorderWidth: '--weavable-section-border-width',
    sectionBorderColor: '--weavable-section-border-color',
    sectionShadow: '--weavable-section-shadow',
    sectionSpacing: '--weavable-section-spacing',
    cardSpacing: '--weavable-card-spacing',

    // Card
    cardBgColor: '--weavable-card-bg-color',
    cardBorderColor: '--weavable-card-border-color',
    cardBorderWidth: '--weavable-card-border-width',
    cardBorderRadius: '--weavable-card-border-radius',
    cardShadow: '--weavable-card-shadow',
    cardShadowHover: '--weavable-card-shadow-hover',
    cardPadding: '--weavable-card-padding',
    cardTextColor: '--weavable-card-text-color',
    cardTextSize: '--weavable-card-text-size',
    cardTextWeight: '--weavable-card-text-weight',
    cardHeadingColor: '--weavable-card-heading-color',
    cardHeadingWeight: '--weavable-card-heading-weight',
    cardHeadingTextTransform: '--weavable-card-heading-text-transform',
    cardHeadingLetterSpacing: '--weavable-card-heading-letter-spacing',
    cardHeadingFont: '--weavable-card-heading-font',
    cardTextFont: '--weavable-card-text-font',
    cardH1Size: '--weavable-card-h1-size',
    cardH2Size: '--weavable-card-h2-size',
    cardH3Size: '--weavable-card-h3-size',
    cardH4Size: '--weavable-card-h4-size',
    cardH5Size: '--weavable-card-h5-size',
    cardH6Size: '--weavable-card-h6-size',

    // Analytics
    analyticsTrendPositiveColor: '--weavable-trend-positive',
    analyticsTrendNegativeColor: '--weavable-trend-negative',
    analyticsTrendNeutralColor: '--weavable-trend-neutral',
    analyticsSparklineColor: '--weavable-sparkline-color',
    analyticsTitleSize: '--weavable-analytics-title-size',
    analyticsTitleWeight: '--weavable-analytics-title-weight',
    analyticsTitleFont: '--weavable-analytics-title-font',
    analyticsValueSize: '--weavable-analytics-value-size',
    analyticsValueWeight: '--weavable-analytics-value-weight',
    analyticsValueFont: '--weavable-analytics-value-font',
    analyticsSupportingTextSize: '--weavable-analytics-supporting-text-size',
    analyticsSupportingTextFont: '--weavable-analytics-supporting-text-font',
    analyticsSparklineLabelSize: '--weavable-analytics-sparkline-label-size',
    analyticsSparklineLabelFont: '--weavable-analytics-sparkline-label-font',
    analyticsTrendSize: '--weavable-analytics-trend-size',
    analyticsTrendWeight: '--weavable-analytics-trend-weight',
    analyticsTrendFont: '--weavable-analytics-trend-font',
    analyticsTrendArrowSize: '--weavable-analytics-trend-arrow-size',
    analyticsColumns: '--weavable-analytics-columns',
};

/**
 * Converts a WeavableTheme object to a Record of CSS variable names to values.
 * Used by WeavableProvider (global defaults) and individual components (overrides).
 */
export function themeToStyles(theme?: Partial<WeavableTheme>): Record<string, string> {
    if (!theme) return {};

    const cssVars: Record<string, string> = {};
    const themeKeys = Object.keys(themeMap) as Array<keyof WeavableTheme>;

    themeKeys.forEach((key) => {
        const value = theme[key];
        if (value != null) {
            cssVars[themeMap[key]] = value;
        }
    });

    return cssVars;
}
